import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { useBUBreakdown } from '../../hooks/useKPIs'
import { formatKpiEur } from '../../lib/formatters'

interface Props {
  metric?: 'revenue' | 'margin' | 'hours'
  period?: 'ytd' | 'year'
  year?: number
}

const METRIC_LABELS: Record<string, string> = {
  revenue: 'Revenue',
  margin:  'Margine',
  hours:   'Ore',
}

function formatValue(value: number, metric: string): string {
  if (metric === 'hours') return `${Math.round(value).toLocaleString('it-IT')} h`
  return formatKpiEur(value)
}

function DeltaBadge({ delta }: { delta: number | null }) {
  if (delta === null) return null
  const positive = delta >= 0
  return (
    <span
      className={`text-xs font-medium px-1.5 py-0.5 rounded ${
        positive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
      }`}
    >
      {positive ? '+' : ''}{delta.toFixed(1)}%
    </span>
  )
}

export function BUBreakdownChart({ metric = 'revenue', period = 'ytd', year }: Props) {
  const { data, isLoading, isError } = useBUBreakdown({ metric, period, year })

  if (isLoading) {
    return (
      <div className="h-48 flex items-center justify-center text-[--km-text-muted] text-sm">
        Caricamento...
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="h-48 flex items-center justify-center text-[--km-text-muted] text-sm">
        Dati non disponibili.
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-[--km-text-muted] text-sm">
        Nessun dato per il periodo selezionato.
      </div>
    )
  }

  const total = data.reduce((s, d) => s + d.value, 0)

  return (
    <div className="space-y-4">
      {/* Donut chart */}
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={80}
            dataKey="value"
            nameKey="bu_name"
            paddingAngle={2}
          >
            {data.map((entry) => (
              <Cell key={entry.bu_id ?? 'unclassified'} fill={entry.bu_color} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: number) => [formatValue(value, metric), METRIC_LABELS[metric]]}
            contentStyle={{
              backgroundColor: 'var(--km-surface)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '8px',
              color: 'var(--km-text-primary)',
            }}
          />
          <Legend
            formatter={(value) => (
              <span className="text-xs text-[--km-text-muted]">{value}</span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Tabella breakdown */}
      <div className="space-y-2">
        {data.map((item) => {
          const pct = total > 0 ? (item.value / total) * 100 : 0
          return (
            <div key={item.bu_id ?? 'unclassified'} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.bu_color }}
                  />
                  <span className="text-[--km-text-primary] font-medium">{item.bu_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <DeltaBadge delta={item.delta_pct} />
                  <span className="text-[--km-text-primary] tabular-nums">
                    {formatValue(item.value, metric)}
                  </span>
                  <span className="text-[--km-text-muted] tabular-nums w-10 text-right">
                    {pct.toFixed(0)}%
                  </span>
                </div>
              </div>
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, backgroundColor: item.bu_color }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
